
import React, { useState, useRef, useEffect } from 'react';
import { useMarkdownFormatter } from '../hooks/useMarkdownFormatter';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { useTheme } from '../context/ThemeContext';
import UserMenu from '../components/UserMenu';
import { apiService } from '../services/api';
import feedbackService from '../services/feedbackService';
import { toast } from 'react-hot-toast';
import { Message } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import '../styles/markdown.css';

// Clean up markdown formatting for API response
function cleanMarkdown(raw: string): string {
  let text = raw;

  // 1. Ensure headings have blank line after them
  text = text.replace(/(#+\s[^\n]+)(?!\n\n)/g, "$1\n");

  // 2. Fix broken words at the end of lines (like "Learnin g" → "Learning")
  text = text.replace(/(\w)\s+(\w)$/gm, "$1$2");

  // 3. Ensure lists start on new lines
  //text = text.replace(/-\s+/g, "\n- ");

  // 4. Ensure paragraphs are separated by a blank line
  text = text.replace(/([^\n])\n([^\n#-])/g, "$1\n$2");

  // 5. Separate special notes (like 👉) into new line
  // Add an extra blank line before the hand/prompt marker so it stands out more
  text = text.replace(/\s*(👉)/g, "\n\n\n$1");

  // Normalize sequences of more than 3 newlines down to 3 to avoid huge gaps
  text = text.replace(/\n{4,}/g, "\n\n\n");

  // Remove an extra blank line that appears before lists so they sit directly after a paragraph
  // Convert 2+ newlines followed by a list marker into a single newline + list marker
  text = text.replace(/\n{2,}([ \t]*(?:[-*+]|\d+\.)\s)/g, "\n$1");

  // 6. Trim extra spaces at line ends
  text = text.replace(/\s+$/gm, "");

  // 7. Collapse single newlines inside paragraph blocks into a single space
  //    - Preserve code fences (```), blockquotes (>), lists (-, *, +, 1.), and headings (#)
  //    - Keep paragraphs separated by blank lines (\n\n)
  try {
    const blocks = text.split(/\n{2,}/g);
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const trimmed = block.trimStart();

      // If block starts with code fence, list marker, heading, or blockquote, leave as-is
      if (/^```/.test(trimmed) || /^[>#\-\*+]|^\d+\.\s/.test(trimmed) || /^#{1,6}\s/.test(trimmed)) {
        continue;
      }

      // Replace single newlines (not multiple) inside this block with a space
      // We first replace windows CRLF then replace single newlines between non-newline chars
      const collapsed = block.replace(/\r\n/g, "\n").replace(/(?<!\n)\n(?!\n)/g, ' ');
      blocks[i] = collapsed;
    }
    text = blocks.join('\n\n');
  } catch (err) {
    // If anything goes wrong, keep original text
    // eslint-disable-next-line no-console
    console.warn('cleanMarkdown: collapse-newlines failed', err);
  }

  return text;
}

// Format Markdown function to preprocess FastAPI response
// Local markdown formatter hook/function
function formatMarkdown(text: string): string {
  // Advanced heading handling
  text = text.split('\n').map((line) => {
    const trimmed = line.trim();
    // 1️⃣ Colon case first (including subheadings)
    if (/^#{1,6}\s.*: .+/.test(trimmed)) {
      const match = trimmed.match(/^(#{1,6}\s.*?):\s*(.+)$/);
      if (match) {
        const heading = match[1];
        const rest = match[2];
        return `${heading}:\n\n${rest.trim()}\n\n`;
      }
    }
    // 1️⃣a Subheading (## ...) ending with colon only
    if (/^##\s.*:$/.test(trimmed)) {
      return trimmed + "\n\n";
    }

    // 2️⃣ Heading ending with colon
    if (/^#{1,6}\s.*:$/.test(trimmed)) {
      return trimmed + "\n\n";
    }

    // 3️⃣ Generic headings
    if (/^#{1,6}\s.*/.test(trimmed)) {
      return trimmed + "\n\n";
    }

    return line;
  }).join('\n');

  return text
    // Headings ending with ':' → break line after colon
    .replace(/^(#{1,6}\s.*:$)/gm, '$1\n\n')
    // Other headings (not ending with colon)
    .replace(/^(#{1,6}\s.*[^:])$/gm, '$1\n\n')
    // Ensure new line before subheadings
    .replace(/(##\s)/g, "\n\n$1")
    // Ensure list items on new lines
    .replace(/(\s*-\s)/g, "\n$1")
    // Add spacing before prompts like 👉
    .replace(/(👉)/g, "\n\n$1")
    // Split long paragraphs (after period + space + Capital letter)
    .replace(/\.\s+(?=[A-Z])/g, ".\n\n")
    .trim();
}

// ChatItem component for individual chat items
const ChatItem: React.FC<{
  chat: any;
  currentChat: any;
  selectChat: (id: string) => void;
  theme: string;
  onRename: (chatId: string, newTitle: string) => void;
  onDelete: (chatId: string) => void;
  reloadChats?: () => void;
}> = ({ chat, currentChat, selectChat, theme, onRename, onDelete, reloadChats }) => {
  const isSelected = currentChat?.id === chat.id;
  const [showMenu, setShowMenu] = React.useState(false);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState(chat.title || '');
  const [isHovered, setIsHovered] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Update newTitle when chat.title changes
  React.useEffect(() => {
    setNewTitle(chat.title || '');
  }, [chat.title]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setIsRenaming(false);
        setNewTitle(chat.title || '');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [chat.title]);

  const handleRename = async () => {
    if (newTitle.trim() && newTitle !== chat.title) {
      await onRename(chat.id, newTitle.trim());
    }
    setIsRenaming(false);
    setShowMenu(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
      setNewTitle(chat.title);
      setShowMenu(false);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleChatClick = () => {
    if (!isRenaming && !showMenu) {
      selectChat(chat.id);
      if (typeof reloadChats === 'function') {
        reloadChats();
      }
    }
  };

  const handleContainerMouseEnter = () => {
    setIsHovered(true);
  };

  const handleContainerMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      onClick={handleChatClick}
      onMouseEnter={handleContainerMouseEnter}
      onMouseLeave={handleContainerMouseLeave}
      style={{
        padding: '0.5rem 0.75rem',
        borderRadius: '0.375rem',
        cursor: isRenaming ? 'default' : 'pointer',
        transition: 'background-color 0.15s ease',
        marginBottom: '0.375rem',
        backgroundColor: isSelected
          ? (theme === 'dark' ? 'rgba(255,255,255,0.20)' : 'rgba(39,71,151,0.30)')
          : (isHovered ? (theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(39,71,151,0.18)') : 'transparent'),
        border: 'none',
        color: theme === 'dark' ? '#ffffff' : '#2d333a',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        boxShadow: 'none',
        minHeight: '2.25rem',
        maxHeight: '2.25rem',
        boxSizing: 'border-box'
      }}
    >
      <div style={{
        flex: 1,
        minWidth: 0,
        paddingLeft: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        minHeight: '1.5rem',
        maxHeight: '1.5rem'
      }}>
        {isRenaming ? (
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={handleRename}
            autoFocus
            style={{
              width: '100%',
              fontSize: '0.875rem',
              fontWeight: 400,
              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
              color: theme === 'dark' ? '#ffffff' : '#1a1a1a',
              border: `1px solid ${theme === 'dark' ? '#404040' : '#d0d0d0'}`,
              borderRadius: '0.25rem',
              padding: '0.25rem 0.5rem',
              outline: 'none'
            }}
          />
        ) : (
          <div style={{
            fontSize: '0.875rem',
            fontWeight: isSelected ? 500 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isSelected
              ? (theme === 'dark' ? '#ffffff' : '#274797')
              : (theme === 'dark' ? '#ffffff' : '#374151'),
            lineHeight: '1.2',
            width: '100%',
            height: '1.25rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            {(chat.title || '').split(' ').slice(0, 4).join(' ')}
          </div>
        )}
      </div>

      {/* Three dots menu - only show when hovered or selected */}
      {(isHovered || isSelected || showMenu) && (
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={handleMenuClick}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.375rem',
              color: theme === 'dark' ? '#ffffff' : '#2d333a',
              fontSize: '1rem',
              lineHeight: 1,
              fontWeight: 'bold',
              opacity: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ⋯
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '-20px',
              backgroundColor: theme === 'dark' ? '#2d333a' : '#ffffff',
              border: `1px solid ${theme === 'dark' ? '#404040' : '#e0e7f0'}`,
              borderRadius: '0.375rem',
              boxShadow: theme === 'dark'
                ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                : '0 4px 12px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              minWidth: '120px',
              overflow: 'hidden',
              marginTop: '0.25rem'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsRenaming(true);
                  setShowMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  color: theme === 'dark' ? '#ffffff' : '#1a1a1a',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#f0f0f0';
                  e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#000000';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#1a1a1a';
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    color: theme === 'dark' ? '#8e9297' : '#6b7280'
                  }}
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                <span>Rename</span>
              </button>
              <div style={{
                height: '1px',
                backgroundColor: theme === 'dark' ? '#404040' : '#e5e5e5',
                margin: '0 0.5rem'
              }} />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(chat.id);
                  setShowMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  color: '#ef4444',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a2a2a' : '#fef2f2';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#ef4444';
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    color: '#ef4444'
                  }}
                >
                  <polyline points="3,6 5,6 21,6"/>
                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ChatPage: React.FC = () => {
  const [messageInput, setMessageInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<{[messageId: string]: 'positive' | 'negative'}>({});
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [showDislikePopup, setShowDislikePopup] = useState<string | null>(null);
  // const [selectedDislikeReason, setSelectedDislikeReason] = useState<string>('');
  const [customDislikeReason, setCustomDislikeReason] = useState<string>('');
  const { user, logout } = useAuth();
  const { chats, currentChat, messages, sendMessage, selectChat, clearCurrentChat, reloadChats } = useChat();
  const { theme, toggleTheme } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize textarea to fit content (like ChatGPT)
  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    try {
      el.style.height = 'auto';
      const max = 150; // px, same as maxHeight
      const newHeight = Math.min(el.scrollHeight, max);
      el.style.height = `${newHeight}px`;
    } catch (err) {
      // ignore
    }
  };

  const handlePositiveFeedback = async (message: Message) => {
    console.log('Submitting feedback for message:', message);
    try {
      // Submit positive feedback to database
      await feedbackService.submitFeedback(
        message.id,       // messageId
        'positive',       // feedbackType
        '',              // reason (not required for positive)
        '',              // additionalInfo
        message.content, // aiResponse
        currentChat?.id || ''     // chatId
      );

      // Update local state
      setMessageFeedback(prev => ({
        ...prev,
        [message.id]: 'positive'
      }));

      toast.success('Thank you for your feedback!');
      console.log('Positive feedback submitted for message:', message.id);
    } catch (error) {
      console.error('Failed to submit positive feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  // Ensure textarea resizes when messageInput changes
  useEffect(() => {
    resizeTextarea();
  }, [messageInput]);

  const handleNegativeFeedback = async (): Promise<void> => {
    if (customDislikeReason.trim() && showDislikePopup) {
      try {
        // Find the message to get its content and chat
        const targetMessage = messages.find((msg: Message) => msg.id === showDislikePopup);
        console.log('Submitting negative feedback for message:', targetMessage?.id, 'Sender:', targetMessage?.sender, 'Content:', targetMessage?.content);

        // Submit feedback to database
        await feedbackService.submitFeedback(
          showDislikePopup,           // messageId
          'negative',                 // feedbackType
          customDislikeReason.trim(), // reason
          '',                        // additionalInfo
          targetMessage?.content || '', // aiResponse
          currentChat?.id || ''     // chatId
        );

        // Update local state
        setMessageFeedback(prev => ({
          ...prev,
          [showDislikePopup]: 'negative'
        }));

        toast.success('Feedback submitted successfully');
        console.log('Dislike feedback submitted for message:', showDislikePopup, {
          reason: customDislikeReason.trim()
        });

        // Close popup and reset state
        setShowDislikePopup(null);
        setCustomDislikeReason('');
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        toast.error('Failed to submit feedback. Please try again.');
      }
    }
  };

  const handleRetryMessage = async (message: Message): Promise<void> => {
    try {
      // Find the user message that prompted this AI response
      const messageIndex = messages.findIndex(msg => msg.id === message.id);
      if (messageIndex > 0) {
        const userMessage = messages[messageIndex - 1];
        if (userMessage.sender === 'user') {
          // Resend the user's message to get a new AI response
          console.log('Retrying message:', userMessage.content);

          // Use the sendMessage function from chat context
          await sendMessage(userMessage.content);

          toast.success('Regenerating response...');
        } else {
          toast.error('Cannot find the original user message to retry.');
        }
      } else {
        toast.error('Cannot retry this message.');
      }
    } catch (error) {
      console.error('Failed to retry message:', error);
      toast.error('Failed to retry message. Please try again.');
    }
  };

  const sendCurrentMessage = async (): Promise<void> => {
    if (messageInput.trim()) {
      const userMessage = messageInput.trim();

      // Clear input immediately like ChatGPT
      setMessageInput('');

      // Instant scroll to bottom when user sends message (ChatGPT-style)
      scrollToBottomInstant();

      // Additional immediate scroll after a tiny delay to ensure DOM updates
      setTimeout(() => {
        scrollToBottomInstant();
      }, 1);

      // Send message (this will add user message immediately and show "Thinking...")
      await sendMessage(userMessage);
    }
  };

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await sendCurrentMessage();
  };

  const handleNewChat = (): void => {
    // Clear the current chat - new chat will be created when first message is sent
    clearCurrentChat();
  };

  // Filter chats based on search query (guard against missing titles)
  const filteredChats = (chats || []).filter((chat: { title?: string }) =>
    (chat.title || '').toLowerCase().includes((searchQuery || '').toLowerCase())
  );

  // Group chats by date
  const groupChatsByDate = (chats: Array<{ updatedAt?: string | number | Date; createdAt: string | number | Date; }>) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groups = {
      today: [] as any[],
      yesterday: [] as any[],
      previous7Days: [] as any[],
      previous30Days: [] as any[],
      older: [] as any[],
    };

    chats.forEach((chat) => {
      const chatDate = new Date(chat.updatedAt || chat.createdAt);
      const chatDateOnly = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());

      if (chatDateOnly.getTime() === today.getTime()) {
        groups.today.push(chat);
      } else if (chatDateOnly.getTime() === yesterday.getTime()) {
        groups.yesterday.push(chat);
      } else if (chatDate >= sevenDaysAgo) {
        groups.previous7Days.push(chat);
      } else if (chatDate >= thirtyDaysAgo) {
        groups.previous30Days.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    return groups;
  };

  const chatGroups = groupChatsByDate(filteredChats);

  // Handle rename chat - update UserConversation table
  const handleRenameChat = async (chatId: string, newTitle: string): Promise<void> => {
    try {
      // Find the conversation ID from the chat ID
      const chat = chats.find((c: { id: string; conversationId?: string }) => c.id === chatId);
      if (!chat || !chat.conversationId) {
        toast.error('Conversation not found');
        return;
      }

      const response = await apiService.updateUserConversation(chat.conversationId, { title: newTitle });

      if (response.success) {
        toast.success('Chat renamed successfully');
        // Reload chats to reflect the change
        await reloadChats();
      } else {
        toast.error('Failed to rename chat');
      }
    } catch (error) {
      console.error('Failed to rename chat:', error);
      toast.error('Failed to rename chat');
    }
  };

  // Handle delete chat - delete from UserConversation table
  const handleDeleteChat = async (chatId: string): Promise<void> => {
    try {
      // Show confirmation dialog
      if (window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
        // Find the conversation ID from the chat ID
        const chat = chats.find((c: { id: string; conversationId?: string }) => c.id === chatId);
        if (!chat || !chat.conversationId) {
          toast.error('Conversation not found');
          return;
        }

        const response = await apiService.deleteUserConversation(chat.conversationId);

        if (response.success) {
          toast.success('Conversation deleted successfully');
          // If the deleted chat was the current chat, clear it
          if (currentChat?.id === chatId) {
            clearCurrentChat();
          }
          // Reload chats to reflect the change
          await reloadChats();
        } else {
          toast.error('Failed to delete conversation');
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  // Auto-scroll to bottom when messages change - with smooth scrolling
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth'): void => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior,
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // Instant scroll to bottom without animation (ChatGPT-style)
  const scrollToBottomInstant = (): void => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'auto',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  useEffect(() => {
    // Instant scroll when messages change (ChatGPT-style)
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 50); // Reduced delay for faster response

    return () => clearTimeout(timer);
  }, [messages]);

  return (
    <>
      {/* Google Fonts Import */}
      <link
        href="https://fonts.googleapis.com/css2?family=Lobster&display=swap"
        rel="stylesheet"
      />

      {/* Custom Scrollbar Styles */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(39,71,151,0.3)'};
            border-radius: 3px;
            border: none;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: ${theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(39,71,151,0.5)'};
          }

          .custom-scrollbar::-webkit-scrollbar-corner {
            background: transparent;
          }

          .chat-scrollbar::-webkit-scrollbar {
            width: 12px;
          }

          .chat-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }

          .chat-scrollbar::-webkit-scrollbar-thumb {
            background-color: ${theme === 'dark' ? '#777777' : '#888888'};
            border-radius: 5px;
            border: none;
          }

          .chat-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: ${theme === 'dark' ? '#888888' : '#666666'};
          }

          .chat-scrollbar::-webkit-scrollbar-corner {
            background: transparent;
          }

          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }

          @keyframes thinking-dot {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% {
              transform: scale(1.2);
              opacity: 1;
            }
          }
        `}
      </style>

      <div style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff'
      }}>
      {/* Left Side Icons - Always Visible */}
      <div style={{
        position: 'relative',
        zIndex: 10
      }}>
        {/* Collapse Icon and Logo */}
        <div style={{
          position: 'fixed',
          top: '0.75rem',
          left: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 1000
        }}>
          {/* Collapse Icon */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              padding: '0.5rem',
              background: 'none',
              border: 'none',
              color: theme === 'dark' ? '#ffffff' : '#2d333a',
              cursor: 'pointer',
              borderRadius: '0.375rem',
              fontSize: '1.25rem',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              outline: 'none',
              boxShadow: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.5)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>


        </div>

        {/* Collapsed Sidebar Icons */}
        <div style={{
          position: 'fixed',
          left: '0',
          top: '0',
          width: sidebarOpen ? '0' : '52px',
          height: '100vh',
          backgroundColor: theme === 'dark' ? '#2d333a' : 'rgba(39,71,151,0.33)',
          borderRight: `1px solid ${theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.2)'}`,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '4.75rem',
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem',
          gap: '0.75rem',
          zIndex: 999,
          willChange: 'width, opacity',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
          overflow: 'hidden',
          transition: 'width 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1.0s ease-in-out',
          opacity: sidebarOpen ? 0 : 1,
          pointerEvents: sidebarOpen ? 'none' : 'auto'
        }}>
          {!sidebarOpen && (
            <>
            {/* New Chat Icon */}
            <button
              onClick={handleNewChat}
              style={{
                padding: '0.5rem',
                background: 'none',
                border: 'none',
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                cursor: 'pointer',
                borderRadius: '0.375rem',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                outline: 'none',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.5)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title="New Chat"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>

            {/* Search Icon */}
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                padding: '0.5rem',
                background: 'none',
                border: 'none',
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                cursor: 'pointer',
                borderRadius: '0.375rem',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                outline: 'none',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.5)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title="Search"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </button>

            {/* History Icon */}
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                padding: '0.5rem',
                background: 'none',
                border: 'none',
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                cursor: 'pointer',
                borderRadius: '0.375rem',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                outline: 'none',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.5)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title="History"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
                <path d="M12 7v5l4 2"/>
              </svg>
            </button>
            </>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? '280px' : '0',
        backgroundColor: theme === 'dark' ? '#2d333a' : 'rgba(39,71,151,0.33)',
        borderRight: `1px solid ${theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.2)'}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        overflow: 'hidden',
        willChange: 'width',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        minWidth: sidebarOpen ? '280px' : '0',
        maxWidth: sidebarOpen ? '280px' : '0'
      }}>
        {/* Sidebar Header */}
        <div style={{
          padding: '4.75rem 0.5rem 0.25rem'
        }}>

          {/* New Chat Button */}
          <button
            onClick={handleNewChat}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              backgroundColor: theme === 'dark' ? 'transparent' : '#274797',
              color: theme === 'dark' ? '#ffffff' : '#ffffff',
              border: `1px solid ${theme === 'dark' ? '#404040' : '#274797'}`,
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '0.5rem',
              transition: 'all 0.15s ease',
              outline: 'none',
              boxShadow: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : 'rgba(39,71,151,0.8)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = theme === 'dark' ? 'transparent' : '#274797';
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <path d="M12 5v14M5 12h14"/>
            </svg>
            New chat
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ padding: '0.75rem 0.5rem 1rem', borderBottom: `1px solid ${theme === 'dark' ? '#333333' : 'rgba(39,71,151,0.2)'}` }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                borderRadius: '0.375rem',
                backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                fontSize: '0.875rem',
                outline: 'none',
                boxShadow: 'none',
                transition: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = 'none';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = theme === 'dark' ? '#404040' : '#e5e5e5';
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = theme === 'dark' ? '#404040' : '#e5e5e5';
              }}
            />
            <svg
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme === 'dark' ? '#8e9297' : '#8e9297',
                pointerEvents: 'none'
              }}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
          </div>
        </div>

        {/* Chat History */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 0.5rem',
          scrollbarWidth: 'thin',
          scrollbarColor: theme === 'dark'
            ? 'rgba(255,255,255,0.2) transparent'
            : 'rgba(39,71,151,0.3) transparent'
        }}
        className="custom-scrollbar"
        >

          {/* Render grouped chats */}
          {chatGroups.today.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '0.375rem 0.75rem 0.25rem',
                marginBottom: '0.125rem'
              }}>
                Today
              </div>
              {chatGroups.today.map((chat) => (
                <ChatItem key={chat.id} chat={chat} currentChat={currentChat} selectChat={selectChat} reloadChats={reloadChats} theme={theme} onRename={handleRenameChat} onDelete={handleDeleteChat} />
              ))}
            </div>
          )}

          {chatGroups.yesterday.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '0.375rem 0.75rem 0.25rem',
                marginBottom: '0.125rem'
              }}>
                Yesterday
              </div>
              {chatGroups.yesterday.map((chat) => (
                <ChatItem key={chat.id} chat={chat} currentChat={currentChat} selectChat={selectChat} theme={theme} onRename={handleRenameChat} onDelete={handleDeleteChat} />
              ))}
            </div>
          )}

          {chatGroups.previous7Days.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '0.375rem 0.75rem 0.25rem',
                marginBottom: '0.125rem'
              }}>
                Previous 7 days
              </div>
              {chatGroups.previous7Days.map((chat) => (
                <ChatItem key={chat.id} chat={chat} currentChat={currentChat} selectChat={selectChat} theme={theme} onRename={handleRenameChat} onDelete={handleDeleteChat} />
              ))}
            </div>
          )}

          {chatGroups.previous30Days.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '0.375rem 0.75rem 0.25rem',
                marginBottom: '0.125rem'
              }}>
                Previous 30 days
              </div>
              {chatGroups.previous30Days.map((chat) => (
                <ChatItem key={chat.id} chat={chat} currentChat={currentChat} selectChat={selectChat} theme={theme} onRename={handleRenameChat} onDelete={handleDeleteChat} />
              ))}
            </div>
          )}

          {chatGroups.older.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '0.375rem 0.75rem 0.25rem',
                marginBottom: '0.125rem'
              }}>
                Older
              </div>
              {chatGroups.older.map((chat) => (
                <ChatItem key={chat.id} chat={chat} currentChat={currentChat} selectChat={selectChat} theme={theme} onRename={handleRenameChat} onDelete={handleDeleteChat} />
              ))}
            </div>
          )}
        </div>


      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme === 'dark' ? '#1a1a1a' : 'rgba(177,187,214,0.60)',
        marginLeft: sidebarOpen ? '0' : '0',
        paddingLeft: sidebarOpen ? '0' : '52px'
      }}>
        {/* Header */}
        <div style={{
          padding: '0.5rem 1.5rem',
          borderBottom: `1px solid ${theme === 'dark' ? '#333333' : '#e0e7f0'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme === 'dark' ? '#1a1a1a' : 'rgba(177,187,214,0.60)',
          height: '56px'
        }}>
          {/* Left Section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1
          }}>
            {/* Logo beside header */}
            <div style={{
              display: 'flex',
              alignItems: 'center'
            }}>
              <img
                src="https://res.cloudinary.com/di83yoe1d/image/upload/f_auto,q_auto/v1/SpaarxSense/led40vf8rgjln1mnxv0q"
                alt="SpaarxSense Logo"
                style={{
                  width: '72px',
                  height: '48px',
                  objectFit: 'contain',
                  filter: theme === 'dark' ? 'brightness(1.2) contrast(1.1)' : 'none'
                }}
                onError={(e) => {
                  console.error('Logo failed to load:', e);
                }}
              />
            </div>

            <div style={{
              color: theme === 'dark' ? '#ffffff' : '#274797',
              fontSize: '28px',
              fontFamily: "'Lobster', cursive",
              marginLeft: '-11px',
              position: 'relative',
              zIndex: 1
            }}>
              <h1 style={{
                margin: 0,
                fontSize: 'inherit',
                fontFamily: 'inherit',
                color: 'inherit',
                fontWeight: 'normal'
              }}>
                SpaarxSense
              </h1>
            </div>
          </div>

          {/* Right Section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 'none'
          }}>
            {/* User Profile with Dropdown Menu */}
            <UserMenu 
              theme={theme}
              onLogout={logout} 
              user={user}
              toggleTheme={toggleTheme}
            />
          </div>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {!currentChat || messages.length === 0 ? (
            // ChatGPT-style welcome screen
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              maxWidth: '768px',
              margin: '0 auto',
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem'
              }}>
                {/* Logo */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <img
                    src="https://res.cloudinary.com/di83yoe1d/image/upload/f_auto,q_auto/v1/SpaarxSense/led40vf8rgjln1mnxv0q"
                    alt="SpaarxSense Logo"
                    style={{
                      width: '64px',
                      height: '48px',
                      objectFit: 'contain',
                      filter: theme === 'dark' ? 'brightness(1.2) contrast(1.1)' : 'none'
                    }}
                    onError={(e) => {
                      console.error('Logo failed to load:', e);
                    }}
                  />
                </div>

                {/* SpaarxSense Text with overlap */}
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'normal',
                  color: theme === 'dark' ? '#ffffff' : '#274797',
                  fontFamily: "'Lobster', cursive",
                  marginLeft: '-10px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  SpaarxSense
                </div>
              </div>
              <div style={{
                fontSize: '1rem',
                color: theme === 'dark' ? '#8e9297' : '#6b7280',
                marginBottom: '2rem',
                lineHeight: 1.5
              }}>
                How can I help you today?
              </div>

              {/* Example prompts */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1rem',
                width: '100%',
                maxWidth: '600px'
              }}>
                {[
                  "Explain quantum computing",
                  "Write a creative story",
                  "Help me plan a project",
                  "Analyze this data"
                ].map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setMessageInput(prompt);
                      // Auto-focus the input
                      setTimeout(() => {
                        const input = document.querySelector('textarea');
                        if (input) input.focus();
                      }, 100);
                    }}
                    style={{
                      padding: '1rem',
                      borderRadius: '0.75rem',
                      border: 'none',
                      backgroundColor: theme === 'dark' ? '#2a2a2a' : 'rgba(39,71,151,0.70)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      boxShadow: theme === 'dark' ? '0 4px 12px rgba(74, 111, 165, 0.3)' : '0 4px 12px rgba(39, 71, 151, 0.3)',
                      transform: 'translateY(0)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#1e1e1e' : 'rgba(39,71,151,0.50)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = theme === 'dark' ? '0 6px 20px rgba(74, 111, 165, 0.4)' : '0 6px 20px rgba(39, 71, 151, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : 'rgba(39,71,151,0.70)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = theme === 'dark' ? '0 4px 12px rgba(74, 111, 165, 0.3)' : '0 4px 12px rgba(39, 71, 151, 0.3)';
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // ChatGPT-style message list
            <div
              className="chat-scrollbar"
              style={{
                flex: 1,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: theme === 'dark' ? '#777777 transparent' : '#888888 transparent'
              }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    padding: '1.5rem 1rem',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={() => setHoveredMessageId(message.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  <div style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                      display: 'flex',
                      alignItems: 'flex-start',
                      /* Keep bubble position: user on right, assistant on left */
                      justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start'
                  }}>
                    {/* Message content */}
                    <div
                      onMouseEnter={() => setHoveredMessageId(message.id)}
                      onMouseLeave={() => setHoveredMessageId(null)}
                      style={{
                        maxWidth: message.sender === 'assistant' ? '100%' : '70%',
                        position: 'relative',
                        marginBottom: message.sender === 'assistant' ? '2.5rem' : '0',
                        willChange: 'auto',
                        backfaceVisibility: 'hidden',
                        transform: 'translateZ(0)'
                      }}>
                      <div style={{
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.95rem',
                        /* Keep bubble positioned (user on right), but force text in questions to be LTR and left-aligned */
                        direction: message.sender === 'user' ? 'ltr' : 'inherit',
                        textAlign: message.sender === 'user' ? 'left' : 'left',
                        padding: '0.75rem 1rem',
                        borderRadius: '1rem',
                        backgroundColor: message.sender === 'user'
                          ? (theme === 'dark' ? '#274797' : '#274797')
                          : (theme === 'dark' ? '#2a2a2a' : '#f0f0f0'),
                        color: message.sender === 'user'
                          ? '#ffffff'
                          : (theme === 'dark' ? '#ffffff' : '#2d333a'),
                        position: 'relative'
                      }}>
                        {/* Show animated thinking dots or regular content */}
                        {message.sender === 'assistant' && message.metadata?.isThinking ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}>
                            <span style={{
                              width: '6px',
                              height: '6px',
                              backgroundColor: theme === 'dark' ? '#ffffff' : '#2d333a',
                              borderRadius: '50%',
                              animation: 'thinking-dot 1.4s infinite ease-in-out',
                              animationDelay: '0s'
                            }} />
                            <span style={{
                              width: '6px',
                              height: '6px',
                              backgroundColor: theme === 'dark' ? '#ffffff' : '#2d333a',
                              borderRadius: '50%',
                              animation: 'thinking-dot 1.4s infinite ease-in-out',
                              animationDelay: '0.2s'
                            }} />
                            <span style={{
                              width: '6px',
                              height: '6px',
                              backgroundColor: theme === 'dark' ? '#ffffff' : '#2d333a',
                              borderRadius: '50%',
                              animation: 'thinking-dot 1.4s infinite ease-in-out',
                              animationDelay: '0.4s'
                            }} />
                          </span>
                        ) : (
                          <>
                            {message.sender === 'assistant' ? (
                              <>
                                <div className="markdown-body prose">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {(() => {
                                      const raw = typeof message.content === 'object' && message.content !== null && 'Answer' in (message.content as Record<string, unknown>)
                                        ? (message.content as Record<string, unknown>).Answer as string
                                        : String(message.content);
                                      return cleanMarkdown(raw);
                                    })()}
                                  </ReactMarkdown>
                                </div>
                                {/* Show source_urls if present, else show documents (never both) */}
                                {/* Show source and documents together, or source_urls if present */}
                                {(() => {
                                  let urls = null;
                                  if (typeof message.content === 'object' && message.content !== null && ((message.content as any).Source_Urls || (message.content as any).source_urls)) {
                                    urls = (message.content as any).Source_Urls || (message.content as any).source_urls;
                                  } else if (message.metadata && (message.metadata.source_urls || message.metadata.Source_Urls)) {
                                    urls = message.metadata.source_urls || message.metadata.Source_Urls;
                                  }
                                  // If urls is non-empty, show only source_urls
                                  if (urls && ((Array.isArray(urls) && urls.length > 0) || (!Array.isArray(urls) && String(urls).trim() !== ''))) {
                                    return (
                                      <div style={{ fontSize: '0.85em', color: '#888', marginTop: '2em' }}>
                                        <span style={{ fontWeight: 500, color: '#505153ff' }}>Source URLs:</span>
                                        {Array.isArray(urls) ? (
                                          <ul style={{ margin: '0.5em 0 0 1.5em', padding: 0, listStyleType: 'disc' }}>
                                            {urls.map((url: string, idx: number) => {
                                              // Try to extract a short label (domain)
                                              let label = url;
                                              try {
                                                const u = new URL(url);
                                                label = u.hostname.replace(/^www\./, '');
                                              } catch {}
                                              return (
                                                <li key={url} style={{ marginBottom: '0.3em', wordBreak: 'break-all', display: 'flex', alignItems: 'center' }}>
                                                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.3em' }}>
                                                    <span style={{ fontSize: '1em', verticalAlign: 'middle' }}>🔗</span>
                                                    <span>{label}</span>
                                                  </a>
                                                </li>
                                              );
                                            })}
                                          </ul>
                                        ) : (
                                          <span style={{ marginLeft: '0.5em', display: 'flex', alignItems: 'center' }}>
                                            <a href={urls} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.3em' }}>
                                              <span style={{ fontSize: '1em', verticalAlign: 'middle' }}>🔗</span>
                                              <span>{(() => {
                                                try {
                                                  const u = new URL(urls);
                                                  return u.hostname.replace(/^www\./, '');
                                                } catch {
                                                  return urls;
                                                }
                                              })()}</span>
                                            </a>
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }
                                  // If urls is empty, show source and documents together
                                  let source = null;
                                  if (typeof message.content === 'object' && message.content !== null && (message.content as any).Source) {
                                    source = (message.content as any).Source;
                                  } else if (message.metadata && message.metadata.source) {
                                    source = message.metadata.source;
                                  }
                                  if (source || (message.metadata && Array.isArray(message.metadata.documents) && message.metadata.documents.length > 0)) {
                                    return (
                                      <div style={{ fontSize: '0.85em', color: '#888', marginTop: '2em' }}>
                                        {source && (
                                          <div style={{ marginBottom: 0 }}>Source: {source}</div>
                                        )}
                                        {message.metadata && Array.isArray(message.metadata.documents) && message.metadata.documents.length > 0 && (
                                          <div style={{ marginTop: 0 }}>
                                            Documents:
                                            <ul style={{ margin: '0.5em 0 0 1em', padding: 0 }}>
                                              {message.metadata.documents.map((doc: any, idx: number) => (
                                                <>
                                                  {doc.file && (
                                                    <li key={doc.file + '-file'} style={{ marginLeft: '1.5em', listStyleType: 'circle' }}>File: <b>{doc.file}</b></li>
                                                  )}
                                                  {doc.collection && (
                                                    <li key={doc.collection + '-collection'} style={{ marginLeft: '1.5em', listStyleType: 'circle' }}>Collection: <b>{doc.collection}</b></li>
                                                  )}
                                                </>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            ) : (
                              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {typeof message.content === 'object' && message.content !== null && 'Answer' in (message.content as Record<string, unknown>) ? (message.content as Record<string, unknown>).Answer as string : String(message.content)}
                              </div>
                            )}
                            {/* Streaming cursor indicator */}
                            {message.sender === 'assistant' && message.metadata?.isStreaming && !message.metadata?.isThinking && (
                              <span style={{
                                display: 'inline-block',
                                width: '1px',
                                height: '1em',
                                backgroundColor: theme === 'dark' ? '#aaa' : '#888',
                                marginLeft: '2px',
                                animation: 'blink 1s infinite',
                                verticalAlign: 'text-bottom',
                                opacity: 0.6
                              }} />
                            )}
                          </>
                        )}

                        {/* Invisible hover bridge for assistant messages */}
                        {message.sender === 'assistant' && (
                          <div style={{
                            position: 'absolute',
                            bottom: '-2rem',
                            left: '0',
                            right: '0',
                            height: '2rem',
                            pointerEvents: 'auto',
                            zIndex: 5
                          }} />
                        )}
                      </div>

                      {/* Action icons for AI messages */}
                      {message.sender === 'assistant' && hoveredMessageId === message.id && (
                        <div style={{
                          position: 'absolute',
                          bottom: '-2rem',
                          left: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          opacity: 1,
                          transition: 'opacity 0.15s ease',
                          zIndex: 10,
                          willChange: 'opacity',
                          backfaceVisibility: 'hidden',
                          transform: 'translateZ(0)',
                          pointerEvents: 'auto',
                          paddingTop: '0.5rem',
                          marginTop: '-0.5rem'
                        }}>
                          {/* Copy button */}
                          <button
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(message.content);
                                setCopiedMessageId(message.id);
                                // Reset after 2 seconds
                                setTimeout(() => {
                                  setCopiedMessageId(null);
                                }, 2000);
                              } catch (error) {
                                console.error('Failed to copy:', error);
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              borderRadius: '0.25rem',
                              color: copiedMessageId === message.id
                                ? '#10a37f'
                                : (theme === 'dark' ? '#8e9297' : '#6b7280'),
                              transition: 'all 0.15s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#f0f0f0';
                              if (copiedMessageId !== message.id) {
                                e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#2d333a';
                              }
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = copiedMessageId === message.id
                                ? '#10a37f'
                                : (theme === 'dark' ? '#8e9297' : '#6b7280');
                            }}
                            title={copiedMessageId === message.id ? "Copied!" : "Copy message"}
                          >
                            {copiedMessageId === message.id ? (
                              // Checkmark icon when copied
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20,6 9,17 4,12"/>
                              </svg>
                            ) : (
                              // Copy icon when not copied
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                              </svg>
                            )}
                          </button>

                          {/* Speaker button */}
                          <button
                            onClick={() => {
                              if ('speechSynthesis' in window) {
                                if (speakingMessageId === message.id) {
                                  // Stop speaking
                                  speechSynthesis.cancel();
                                  setSpeakingMessageId(null);
                                } else {
                                  // Start speaking
                                  speechSynthesis.cancel(); // Stop any current speech
                                  const utterance = new SpeechSynthesisUtterance(message.content);
                                  utterance.rate = 0.9;
                                  utterance.pitch = 1;
                                  utterance.volume = 0.8;

                                  utterance.onstart = () => {
                                    setSpeakingMessageId(message.id);
                                  };

                                  utterance.onend = () => {
                                    setSpeakingMessageId(null);
                                  };

                                  utterance.onerror = () => {
                                    setSpeakingMessageId(null);
                                  };

                                  speechSynthesis.speak(utterance);
                                }
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              borderRadius: '0.25rem',
                              color: speakingMessageId === message.id
                                ? '#10a37f'
                                : (theme === 'dark' ? '#8e9297' : '#6b7280'),
                              transition: 'all 0.15s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#f0f0f0';
                              if (speakingMessageId !== message.id) {
                                e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#2d333a';
                              }
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = speakingMessageId === message.id
                                ? '#10a37f'
                                : (theme === 'dark' ? '#8e9297' : '#6b7280');
                            }}
                            title={speakingMessageId === message.id ? "Stop reading" : "Read aloud"}
                          >
                            {speakingMessageId === message.id ? (
                              // Stop/Pause icon when speaking
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="6" y="4" width="4" height="16"/>
                                <rect x="14" y="4" width="4" height="16"/>
                              </svg>
                            ) : (
                              // Speaker icon when not speaking
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g id="fluent:speaker-2-16-filled">
                                  <path id="Vector" d="M13.5 3.74999C13.5 3.60248 13.4565 3.45824 13.3749 3.33533C13.2933 3.21243 13.1773 3.1163 13.0414 3.05899C12.9055 3.00167 12.7557 2.98571 12.6107 3.01311C12.4658 3.0405 12.3321 3.11002 12.2265 3.21299L7.836 7.48799H5.25C4.65326 7.48799 4.08097 7.72504 3.65901 8.147C3.23705 8.56895 3 9.14125 3 9.73799V14.223C3 14.8197 3.23705 15.392 3.65901 15.814C4.08097 16.2359 4.65326 16.473 5.25 16.473H7.8345L12.225 20.7855C12.3305 20.8889 12.4641 20.9588 12.6092 20.9865C12.7542 21.0143 12.9043 20.9986 13.0404 20.9414C13.1766 20.8842 13.2928 20.7881 13.3746 20.6651C13.4564 20.5421 13.5 20.3977 13.5 20.25V3.74999ZM15.1665 7.78349C15.2278 7.70632 15.3038 7.64199 15.39 7.59419C15.4762 7.54639 15.5709 7.51605 15.6689 7.50491C15.7668 7.49376 15.866 7.50203 15.9607 7.52924C16.0555 7.55645 16.1439 7.60207 16.221 7.66349L16.224 7.66499L16.227 7.66799L16.2345 7.67399L16.257 7.69349L16.326 7.75349C16.381 7.80349 16.452 7.87449 16.539 7.96649C16.7085 8.15099 16.929 8.41949 17.1465 8.77649C17.583 9.49649 18.006 10.566 18.006 11.9985C18.006 13.4295 17.583 14.5005 17.1465 15.2205C16.9715 15.5105 16.7679 15.7823 16.539 16.032C16.4427 16.1347 16.3415 16.2328 16.236 16.326L16.224 16.3365H16.2225C16.2225 16.3365 15.552 16.701 15.168 16.2195C15.0445 16.065 14.9871 15.868 15.0081 15.6713C15.0292 15.4747 15.1271 15.2943 15.2805 15.1695L15.2835 15.1665L15.3105 15.1425C15.3385 15.1175 15.381 15.075 15.438 15.015C15.5986 14.8386 15.7413 14.6466 15.864 14.442C16.179 13.9245 16.506 13.119 16.506 11.997C16.506 10.875 16.179 10.0725 15.864 9.55649C15.7095 9.30206 15.5241 9.06777 15.312 8.85899L15.285 8.83499C15.1303 8.71099 15.0309 8.53075 15.0087 8.3337C14.9865 8.13665 15.0432 7.93882 15.1665 7.78349ZM17.7195 4.66349C17.6427 4.59994 17.554 4.55234 17.4586 4.52346C17.3633 4.49458 17.2631 4.485 17.1639 4.49529C17.0648 4.50557 16.9687 4.53551 16.8813 4.58336C16.7938 4.6312 16.7168 4.696 16.6547 4.77395C16.5926 4.8519 16.5467 4.94145 16.5196 5.03737C16.4925 5.13328 16.4848 5.23363 16.4969 5.33256C16.509 5.43148 16.5408 5.527 16.5902 5.61353C16.6397 5.70005 16.7059 5.77585 16.785 5.83649L16.8015 5.85149L16.8795 5.91899C16.9515 5.98199 17.052 6.07949 17.181 6.21149C17.436 6.47699 17.781 6.87599 18.126 7.40249C18.816 8.45399 19.506 10.002 19.506 12.006C19.5119 13.6414 19.0315 15.2416 18.126 16.6035C17.781 17.1285 17.436 17.5245 17.181 17.7885C17.0603 17.9139 16.9342 18.034 16.803 18.1485L16.7865 18.1635H16.785C16.6336 18.2888 16.5375 18.4684 16.5172 18.6639C16.4969 18.8593 16.5541 19.0549 16.6766 19.2086C16.799 19.3623 16.9769 19.4618 17.1719 19.4857C17.3669 19.5096 17.5636 19.456 17.7195 19.3365L17.769 19.296L17.8815 19.1985C17.976 19.113 18.1065 18.9915 18.261 18.831C18.677 18.399 19.0517 17.9289 19.38 17.427C20.4455 15.8203 21.0109 13.9339 21.0045 12.006C21.0085 10.0766 20.4434 8.18888 19.38 6.57899C19.0516 6.07659 18.6775 5.60559 18.2625 5.17199C18.1056 5.00915 17.9414 4.85349 17.7705 4.70549L17.736 4.67699L17.7255 4.66799L17.7225 4.66499L17.7195 4.66349Z" fill="currentColor"/>
                                </g>
                              </svg>
                            )}
                          </button>

                          {/* Feedback buttons */}
                          {message.sender === 'assistant' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              {/* Retry/Resend button */}
                              <button
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  handleRetryMessage(message);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  borderRadius: '0.25rem',
                                  color: theme === 'dark' ? '#8e9297' : '#6b7280',
                                  transition: 'all 0.15s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#f0f0f0';
                                  e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#2d333a';
                                }}
                                onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = theme === 'dark' ? '#8e9297' : '#6b7280';
                                }}
                                title="Regenerate response"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                                  <path d="M21 3v5h-5"/>
                                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                                  <path d="M3 21v-5h5"/>
                                </svg>
                              </button>

                              {/* Thumbs up button - show if no feedback or positive feedback */}
                              {(!messageFeedback[message.id] || messageFeedback[message.id] === 'positive') && (
                                <button
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    handlePositiveFeedback(message);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    borderRadius: '0.25rem',
                                    color: messageFeedback[message.id] === 'positive' 
                                      ? '#10a37f' 
                                      : (theme === 'dark' ? '#8e9297' : '#6b7280'),
                                    transition: 'all 0.15s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#f0f0f0';
                                    if (messageFeedback[message.id] !== 'positive') {
                                      e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#2d333a';
                                    }
                                  }}
                                  onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = messageFeedback[message.id] === 'positive'
                                      ? '#10a37f'
                                      : (theme === 'dark' ? '#8e9297' : '#6b7280');
                                  }}
                                  title={messageFeedback[message.id] === 'positive' ? 'Thanks for your feedback!' : 'Good response'}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                                  </svg>
                                </button>
                              )}

                              {/* Thumbs down button - show if no feedback or negative feedback */}
                              {(!messageFeedback[message.id] || messageFeedback[message.id] === 'negative') && (
                                <button
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    setShowDislikePopup(message.id);
                                    setCustomDislikeReason('');
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    borderRadius: '0.25rem',
                                    color: messageFeedback[message.id] === 'negative'
                                      ? '#e53e3e'
                                      : (theme === 'dark' ? '#8e9297' : '#6b7280'),
                                    transition: 'all 0.15s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#f0f0f0';
                                    if (messageFeedback[message.id] !== 'negative') {
                                      e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#2d333a';
                                    }
                                  }}
                                  onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = messageFeedback[message.id] === 'negative'
                                      ? '#e53e3e'
                                      : (theme === 'dark' ? '#8e9297' : '#6b7280');
                                  }}
                                  title={messageFeedback[message.id] === 'negative' ? 'Feedback submitted' : 'Needs improvement'}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V3H6.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}



              {/* Auto-scroll anchor */}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div style={{
          /* reduced overall bottom area padding by ~25% */
          padding: '0.14rem 1.125rem 0.56rem',
          backgroundColor: 'transparent',
          position: 'relative',
          willChange: 'auto',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)'
        }}>
          <form onSubmit={handleSendMessage} style={{
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative'
          }}>
            <div style={{
              position: 'relative',
              border: `1px solid ${theme === 'dark' ? '#555555' : '#d0d0d0'}`,
              /* slightly smaller radius to match reduced area */
              borderRadius: '0.85rem',
              backgroundColor: theme === 'dark' ? '#3a3a3a' : '#ffffff',
              transition: 'none',
              boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}>
              <textarea
                ref={(el) => { textareaRef.current = el; }}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onInput={() => resizeTextarea()}
                placeholder="Message SpaarxSense..."
                rows={1}
                style={{
                  width: '100%',
                  minHeight: '46px',
                  maxHeight: '150px',
                  padding: '0.75rem 3rem 0.75rem 1rem',
                  border: 'none',
                  borderRadius: '0.9375rem',
                  backgroundColor: 'transparent',
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  /* Force left-to-right and left aligned text for the question/input area */
                  direction: 'ltr',
                  textAlign: 'left',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  lineHeight: '1.4',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
                className="no-scrollbar"
                onFocus={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = 'none';
                  e.target.style.border = 'none';
                }}
                onBlur={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = 'none';
                  e.target.style.border = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendCurrentMessage();
                  }
                }}
              />
              <button
                type="submit"
                disabled={!messageInput.trim()}
                style={{
                  position: 'absolute',
                  right: '0.625rem',
                  /* anchor to the bottom so the button remains static while the textarea grows */
                  bottom: '0.75rem',
                  /* slightly smaller send button to better fit the reduced area */
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  minHeight: '28px',
                  maxWidth: '28px',
                  maxHeight: '28px',
                  border: 'none',
                  borderRadius: '16px',
                  backgroundColor: !messageInput.trim() ? (theme === 'dark' ? '#404040' : '#d0d0d0') : '#274797',
                  color: 'white',
                  cursor: !messageInput.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  fontSize: '0.8rem',
                  boxShadow: !messageInput.trim() ? 'none' : '0 2px 4px rgba(39, 71, 151, 0.3)',
                  outline: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  padding: '0',
                  margin: '0',
                  boxSizing: 'border-box',
                  flexShrink: 0
                }}
                onMouseOver={(e) => {
                  if (messageInput.trim()) {
                    e.currentTarget.style.backgroundColor = '#1e3a7a';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(39, 71, 151, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  if (messageInput.trim()) {
                    e.currentTarget.style.backgroundColor = '#274797';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(39, 71, 151, 0.3)';
                  }
                }}
              >
                <img
                  src="data:image/svg+xml,%3csvg%20width='19'%20height='16'%20viewBox='0%200%2019%2016'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20id='Vector'%20d='M0%2016V10L8%208L0%206V0L19%208L0%2016Z'%20fill='%23274797'/%3e%3c/svg%3e"
                  alt="Send"
                  width="16"
                  height="13"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </button>
            </div>
            <div style={{
              textAlign: 'center',
              /* reduce small top gap by 25% */
              marginTop: '9px'
            }}>
              <span style={{
                fontSize: '13px',
                color: theme === 'dark' ? '#a0aec0' : '#718096',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontWeight: '400',
                letterSpacing: '0.01em'
              }}>
                SpaarxSense can make mistakes. Check important info.
              </span>
            </div>
          </form>
        </div>
      </div>

      {/* Dislike Popup */}
      {showDislikePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#274797',
            padding: '1.25rem',
            width: '24rem',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              fontSize: '1.125rem',
              color: 'white',
              margin: 0,
              marginBottom: '1.375rem',
              textAlign: 'left'
            }}>
              Reason for Dislike
            </h2>

            <div style={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              {[
                'Answer was not helpful',
                'Response was inaccurate',
                'Too slow to respond',
                'Not relevant to my question',
                'Confusing or unclear',
                'Not Convincing'
              ].map((reason) => (
                <button
                  key={reason}
                  onClick={() => {
                    setCustomDislikeReason(reason);
                  }}
                  style={{
                    fontSize: '0.625rem',
                    width: 'fit-content',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    color: 'white',
                    backgroundColor: '#2c4074',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#455e9d';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#2c4074';
                  }}
                >
                  {reason}
                </button>
              ))}
            </div>

            <textarea
              value={customDislikeReason}
              onChange={(e) => setCustomDislikeReason(e.target.value)}
              placeholder="Or enter your own reason..."
              style={{
                border: 'none',
                padding: '0.5rem',
                width: '100%',
                outline: 'none',
                borderRadius: '0.75rem',
                backgroundColor: 'white',
                color: '#374151',
                marginBottom: '1rem',
                minHeight: '80px',
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: '0.875rem'
              }}
            />

            <div style={{
              marginTop: '0.5rem',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <button
                onClick={() => {
                  setShowDislikePopup(null);
                  setCustomDislikeReason('');
                }}
                style={{
                  backgroundColor: '#455e9d',
                  color: 'white',
                  paddingLeft: '1rem',
                  paddingRight: '1rem',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderRadius: '0.25rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#2c4074';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#455e9d';
                }}
              >
                Cancel
              </button>

              <button
                onClick={() => handleNegativeFeedback()}
                disabled={!customDislikeReason.trim()}
                style={{
                  backgroundColor: customDislikeReason.trim() ? '#455e9d' : '#6b7280',
                  color: 'white',
                  paddingLeft: '1rem',
                  paddingRight: '1rem',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderRadius: '0.25rem',
                  border: 'none',
                  cursor: customDislikeReason.trim() ? 'pointer' : 'not-allowed',
                  opacity: customDislikeReason.trim() ? 1 : 0.5,
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => {
                  if (customDislikeReason.trim()) {
                    e.currentTarget.style.backgroundColor = '#2c4074';
                  }
                }}
                onMouseOut={(e) => {
                  if (customDislikeReason.trim()) {
                    e.currentTarget.style.backgroundColor = '#455e9d';
                  }
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ChatPage;
